import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';

import { resendVerification } from '@mysimpledb/shared/data/auth';
import AppButton from '@mysimpledb/shared/components/UI/Button/Button';

import './EmailNotVerified.css';

export default function EmailNotVerified() {
  const location = useLocation();
  const navigate = useNavigate();

  const emailFromState = location.state?.email || '';

  const [email, setEmail] = useState(emailFromState);
  const [status, setStatus] = useState('');
  const [statusType, setStatusType] = useState('');
  const [sending, setSending] = useState(false);

  const canSend = useMemo(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    return emailRegex.test(email.trim());
  }, [email]);

  const handleResend = async () => {
    if (!canSend || sending) return;

    setStatus('');
    setStatusType('');
    setSending(true);

    try {
      await resendVerification(email.trim());

      setStatus(
        'Verification email sent. Please check your inbox and spam or junk folder.'
      );
      setStatusType('success');
    } catch (err) {
      console.error('Unable to resend verification email:', err);

      setStatus(
        'Unable to resend the verification email right now. Please try again in a moment.'
      );
      setStatusType('error');
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="email-not-verified-page">
      <section
        className="email-not-verified-card"
        aria-labelledby="email-not-verified-title"
      >
        <div className="email-not-verified-icon" aria-hidden="true">
          ✉
        </div>

        <div className="email-not-verified-heading">
          <div className="email-not-verified-eyebrow">Account verification</div>

          <h1 id="email-not-verified-title">Email not verified</h1>

          <p>
            You need to verify your email address before signing in to
            MySimpleDB.
          </p>
        </div>

        <div className="email-not-verified-message">
          We sent a verification message when your account was created. Enter
          your email address below to send another copy.
        </div>

        <div className="email-not-verified-form">
          <label htmlFor="verification-email">Email address</label>

          <input
            id="verification-email"
            type="email"
            value={email}
            readOnly
            aria-readonly="true"
            tabIndex={-1}
          />

          <AppButton
            variant="solid"
            size="md"
            onPress={handleResend}
            disabled={!canSend || sending}
            style={{ width: '100%' }}
          >
            {sending ? 'Sending…' : 'Resend verification email'}
          </AppButton>

          {status && (
            <div
              className={`email-not-verified-status ${
                statusType === 'success'
                  ? 'email-not-verified-status--success'
                  : 'email-not-verified-status--error'
              }`}
              role={statusType === 'error' ? 'alert' : 'status'}
            >
              {status}
            </div>
          )}
        </div>

        <div className="email-not-verified-divider">
          <span>or</span>
        </div>

        <div className="email-not-verified-actions">
          <AppButton
            variant="outline"
            size="md"
            onPress={() => navigate('/login')}
            style={{ width: '100%' }}
          >
            Back to Login
          </AppButton>

          <div className="email-not-verified-create">
            Don&apos;t have an account?{' '}
            <Link to="/create-account">Create a new account</Link>
          </div>
        </div>

        <div className="email-not-verified-help">
          Still having trouble? Contact{' '}
          <a href="mailto:support@mysimpledb.com">support@mysimpledb.com</a>
        </div>
      </section>
    </main>
  );
}
